<template>
  <div class="sm-page">
    <div class="sm-header">
      <div>
        <p class="sm-kicker">Social media</p>
        <h1 class="sm-title">Channels</h1>
        <p class="sm-subtitle">Your connected channels and their performance. Pick a channel to see its feed, or create a post from a capability.</p>
      </div>
    </div>

    <div class="sm-layout">
      <!-- Channels -->
      <aside class="sm-channels" data-testid="sm-channels">
        <div class="sm-channels-head">
          <span class="sm-section-label">Channels</span>
          <a v-if="store.manageUrl" :href="store.manageUrl" target="_blank" rel="noopener" class="sm-btn sm-btn-ghost sm-btn-sm" data-testid="sm-add-channel">+ Add channel</a>
        </div>

        <p v-if="store.loadingChannels" class="sm-muted">Loading channels…</p>
        <div v-else-if="store.channelsStatus === 'not_configured'" class="sm-empty-note">
          Postiz isn't connected for this workspace. <a v-if="store.manageUrl" :href="store.manageUrl" target="_blank" rel="noopener" class="sm-link">Connect channels →</a>
        </div>
        <p v-else-if="!store.channels.length" class="sm-muted">No channels connected. <a v-if="store.manageUrl" :href="store.manageUrl" target="_blank" rel="noopener" class="sm-link">Add one →</a></p>

        <button
          v-for="c in store.channels"
          :key="c.integration_id"
          class="sm-channel"
          :class="{ active: c.platform === selectedPlatform }"
          :data-testid="`sm-channel-${c.platform}`"
          @click="selectChannel(c)"
        >
          <div class="sm-channel-top">
            <span class="sm-badge" :data-platform="c.platform">{{ platformShort(c.platform) }}</span>
            <span class="sm-channel-name">{{ c.name }}</span>
          </div>
          <template v-if="c.performance.has_analytics">
            <div class="sm-perf">
              <span class="sm-perf-item"><b>{{ c.performance.followers != null ? '+' + fmt(c.performance.followers) : '—' }}</b> new followers</span>
              <span class="sm-perf-item"><b>{{ fmt(c.performance.impressions) }}</b> views</span>
              <span class="sm-perf-item"><b>{{ fmt(c.performance.clicks) }}</b> clicks</span>
            </div>
            <div class="sm-perf-period">last 30 days · from Postiz</div>
          </template>
          <div v-else class="sm-perf-none">No analytics for this channel</div>
        </button>
      </aside>

      <!-- Channel feed -->
      <section class="sm-feed" data-testid="sm-feed">
        <div class="sm-feed-head">
          <div>
            <h2 class="sm-feed-title">{{ selectedChannel ? selectedChannel.name : 'Select a channel' }}</h2>
            <p v-if="selectedChannel" class="sm-muted">{{ platformLabel(selectedPlatform) }} · feed</p>
          </div>
          <button v-if="selectedChannel" class="sm-btn sm-btn-primary" data-testid="sm-new-post" @click="openNewPost">+ New post</button>
        </div>

        <p v-if="!selectedChannel" class="sm-state">Pick a channel on the left to see its posts.</p>
        <p v-else-if="store.loadingFeed" class="sm-muted">Loading feed…</p>
        <p v-else-if="!feed.length" class="sm-state">No posts on this channel yet — create one.</p>

        <div v-else class="sm-posts">
          <article v-for="item in feed" :key="item.id" class="sm-post" :data-testid="`sm-post-${item.postId}`">
            <div class="sm-post-head">
              <span class="sm-pill" :data-status="item.status">{{ item.status }}</span>
              <span class="sm-post-time">{{ fmtDate(item.at) }}</span>
            </div>
            <p class="sm-post-content">{{ item.content }}</p>
            <img v-if="item.image" :src="item.image" class="sm-post-image" alt="" />
          </article>
        </div>
      </section>
    </div>

    <!-- New post — chat sidebar -->
    <NewPostSidebar
      :open="sidebarOpen"
      :site-id="siteId"
      :channel="selectedChannel"
      :capabilities="capabilities"
      @close="sidebarOpen = false"
      @posted="onPosted"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSocialStore } from '../stores/social'
import { useCapabilitiesStore } from '../stores/capabilities'
import NewPostSidebar from '../components/NewPostSidebar.vue'

const route = useRoute()
const store = useSocialStore()
const capsStore = useCapabilitiesStore()

const siteId = computed(() => route.params.id)
const selectedPlatform = ref(null)
const selectedChannel = ref(null)
const capabilities = computed(() => capsStore.capabilities || [])

const PLATFORM_LABELS = {
  'linkedin-page': 'LinkedIn', linkedin: 'LinkedIn', x: 'X', twitter: 'X',
  instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', tiktok: 'TikTok',
  threads: 'Threads', pinterest: 'Pinterest', mastodon: 'Mastodon',
}
const platformLabel = (p) => PLATFORM_LABELS[p] || (p || '').replace('-', ' ')
const platformShort = (p) => (platformLabel(p)[0] || '?').toUpperCase()
const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—')

const feed = computed(() => (selectedPlatform.value ? store.feedFor(selectedPlatform.value) : []))
const sidebarOpen = ref(false)

function selectChannel(c) {
  selectedChannel.value = c
  selectedPlatform.value = c.platform
}

function openNewPost() { sidebarOpen.value = true }
async function onPosted() { await store.loadFeed(siteId.value) }

async function boot(id) {
  await Promise.all([store.loadChannels(id), store.loadFeed(id), capsStore.loadCoverage(id)])
  if (store.channels.length && !selectedChannel.value) selectChannel(store.channels[0])
}
onMounted(() => boot(siteId.value))
watch(siteId, (id) => { if (id) { selectedChannel.value = null; selectedPlatform.value = null; sidebarOpen.value = false; boot(id) } })
</script>

<style scoped>
.sm-page { max-width: 1100px; margin: 0 auto; padding: 24px 16px 48px; color: var(--text-primary); }
.sm-header { margin-bottom: 18px; }
.sm-kicker { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); margin: 0; }
.sm-title { font-size: 1.5rem; font-weight: 700; margin: 2px 0 4px; }
.sm-subtitle { color: var(--text-secondary); font-size: 0.9rem; margin: 0; max-width: 640px; }
.sm-section-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-secondary); }
.sm-muted { color: var(--text-secondary); font-size: 0.85rem; margin: 8px 0; }
.sm-state { text-align: center; color: var(--text-secondary); padding: 48px 16px; }
.sm-link { color: var(--accent-light); }

.sm-layout { display: grid; grid-template-columns: 320px 1fr; gap: 16px; align-items: start; }

.sm-channels { display: flex; flex-direction: column; gap: 8px; }
.sm-channels-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.sm-channel {
  text-align: left; background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md);
  padding: 12px 14px; cursor: pointer; color: var(--text-primary); transition: background 0.15s, border-color 0.15s;
}
.sm-channel:hover { background: var(--glass-bg-hover); }
.sm-channel.active { border: var(--glass-border-hi); background: var(--glass-bg-active); }
.sm-channel-top { display: flex; align-items: center; gap: 10px; }
.sm-channel-name { font-weight: 600; font-size: 0.9rem; }
.sm-badge { width: 26px; height: 26px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; background: var(--glass-bg-active); color: var(--accent-light); flex: none; }
.sm-badge[data-platform='linkedin-page'], .sm-badge[data-platform='linkedin'] { background: rgba(56,189,248,0.18); color: #7cc7f5; }
.sm-badge[data-platform='x'], .sm-badge[data-platform='twitter'] { background: rgba(255,255,255,0.12); color: #fff; }
.sm-badge[data-platform='instagram'] { background: rgba(236,72,153,0.18); color: #f9a8d4; }
.sm-perf { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 10px; font-size: 0.78rem; color: var(--text-secondary); }
.sm-perf-item b { color: var(--text-primary); font-variant-numeric: tabular-nums; }
.sm-perf-none { margin-top: 10px; font-size: 0.75rem; color: var(--text-muted); }
.sm-perf-period { margin-top: 6px; font-size: 0.68rem; color: var(--text-muted); }

.sm-feed { min-width: 0; }
.sm-feed-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.sm-feed-title { font-size: 1.15rem; font-weight: 700; margin: 0 0 2px; }
.sm-posts { display: flex; flex-direction: column; gap: 12px; }
.sm-post { background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md); padding: 14px; backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm); }
.sm-post-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.sm-post-time { font-size: 0.76rem; color: var(--text-muted); margin-left: auto; }
.sm-post-content { color: var(--text-primary); font-size: 0.9rem; line-height: 1.55; white-space: pre-wrap; margin: 0; }
.sm-post-image { margin-top: 10px; max-width: 240px; border-radius: var(--radius-sm); display: block; }

.sm-pill { font-size: 0.68rem; font-weight: 700; border-radius: 999px; padding: 2px 9px; background: var(--glass-bg-active); color: var(--text-secondary); text-transform: capitalize; }
.sm-pill[data-status='published'] { background: rgba(52,211,153,0.18); color: var(--success); }
.sm-pill[data-status='scheduled'] { background: rgba(56,189,248,0.18); color: #93c5fd; }
.sm-pill[data-status='draft'] { background: rgba(251,191,36,0.18); color: var(--warning); }
.sm-pill[data-status='failed'] { background: rgba(248,113,113,0.18); color: var(--danger); }

.sm-btn { border-radius: var(--radius-sm); padding: 8px 14px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: all 0.15s; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
.sm-btn-sm { padding: 5px 10px; font-size: 0.78rem; }
.sm-btn-primary { background: var(--accent); color: #fff; box-shadow: 0 0 18px var(--accent-glow); }
.sm-btn-primary:hover { background: var(--accent-light); }
.sm-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
.sm-btn-ghost { background: var(--glass-bg); border: var(--glass-border); color: var(--text-primary); }
.sm-btn-ghost:hover { background: var(--glass-bg-hover); }

/* Modal */


@media (max-width: 820px) { .sm-layout { grid-template-columns: 1fr; } }
</style>
